/**
 * Compare Size widget — theme port of the Intelligems DS_01/DS_05 personalization.
 *
 * The markup is rendered server-side by snippets/size-compare.liquid. This script wires up the
 * behaviour the experiment's injected JS used to provide:
 *   - open the popup on a (trusted) click of the "Compare Size" trigger or a size option
 *   - switch the Standard/Thick tabs (swaps the visible card + hint)
 *   - "Choose Standard/Thick" clicks the real theme size radio (handled by assets/size-pdp.js),
 *     then closes the popup
 *
 * The popup is a modal dialog (aria-modal): opening moves focus into it, Escape and a click on
 * the close button / overlay dismiss it, focus is trapped while open, and focus returns to
 * whatever opened it on close.
 *
 * No viewport gating — CSS handles the responsive bottom-sheet vs modal. No analytics; the
 * experiment's tracking stays in Intelligems.
 */
const ready = (callback) => {
  if (document.readyState !== 'loading') callback();
  else document.addEventListener('DOMContentLoaded', callback);
};

ready(() => {
  const root = document.querySelector('.size-compare');
  if (!root) return;

  // Move the popup to <body> so position:fixed is relative to the viewport and it stacks
  // above the rest of the page. The size selector lives inside ancestors that create
  // stacking/transform contexts (gallery sliders, etc.), which would otherwise trap the
  // fixed popup and let later page sections paint on top of it. Mirrors how the original
  // Intelligems build injected the popup directly into <body>.
  if (root.parentElement !== document.body) {
    document.body.appendChild(root);
  }

  const sizeSelection = document.querySelector('#dcop-size-selection');
  // The real theme radios our CTAs proxy to. assets/size-pdp.js handles selection on click.
  const standardOption = sizeSelection && sizeSelection.querySelector('.new-content .variant-option');
  const thickOption = sizeSelection && sizeSelection.querySelector('.dropdown-content > .variant-option');

  const dialog = root.querySelector('.size-compare__dialog');
  const isOpen = () => root.classList.contains('size-compare--open');

  // Remember what had focus before opening so we can restore it on close (a11y: focus should
  // not get stranded on the now-hidden popup).
  let lastFocused = null;

  const open = () => {
    if (isOpen()) return;
    lastFocused = document.activeElement;
    root.classList.add('size-compare--open');
    // Move focus into the dialog so keyboard/screen-reader users land inside the modal.
    (root.querySelector('.size-compare__close') || dialog).focus();
  };

  const close = () => {
    if (!isOpen()) return;
    root.classList.remove('size-compare--open');
    // Restore focus to whatever opened the popup (the trigger or a size option).
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    lastFocused = null;
  };

  const activateTab = (size) => {
    root.querySelectorAll('.size-compare__tab').forEach((tab) => {
      tab.classList.toggle('size-compare__tab--active', tab.dataset.size === size);
    });
    root.querySelectorAll('.size-compare__card').forEach((card) => {
      card.classList.toggle('size-compare__card--active', card.dataset.size === size);
    });
    root.querySelectorAll('.size-compare__hint-content').forEach((hint) => {
      hint.classList.toggle('size-compare__hint-content--active', hint.dataset.size === size);
    });
  };

  // Clicks inside the popup
  root.addEventListener('click', (event) => {
    const tab = event.target.closest('.size-compare__tab');
    const dismiss = event.target.closest('.size-compare__close, .size-compare__overlay');
    const cta = event.target.closest('.size-compare__cta');

    if (tab) {
      activateTab(tab.dataset.size);
    } else if (dismiss) {
      close();
    } else if (cta) {
      const standardActive = root.querySelector('.size-compare__tab--standard.size-compare__tab--active');
      if (standardActive) {
        if (standardOption) standardOption.click();
      } else if (thickOption) {
        thickOption.click();
      }
      close();
    }
  });

  // Keyboard handling while the modal is open: Escape closes it, Tab is trapped inside the
  // dialog so focus can't wander to the page behind the overlay.
  root.addEventListener('keydown', (event) => {
    if (!isOpen()) return;
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = dialog.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const visible = Array.prototype.filter.call(focusable, (el) => el.offsetParent !== null);
    if (!visible.length) return;

    const first = visible[0];
    const last = visible[visible.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  // Open the popup on a genuine user click of the trigger or a size option. isTrusted guards
  // against the programmatic .click() above re-opening the popup right after "Choose ...".
  document.addEventListener('click', (event) => {
    if (!event.isTrusted) return;
    if (event.target.closest('.size-compare__trigger')) {
      open();
    } else if (event.target.closest('#dcop-size-selection .new-content .variant-option')) {
      activateTab('Standard');
      open();
    } else if (event.target.closest('#dcop-size-selection .dropdown-content > .variant-option')) {
      activateTab('Thick');
      open();
    }
  });
});
